import { v4 as uuid } from 'uuid';

const key = (projectId: number) => `sidebar_project_${projectId}`;
import iframeOrExtPostMessage from '../../../shared/utils/iframeOrExtPostMessage';
import { Tag } from '../CreateTask/create_task/types';
import { Emoji } from '../../../../javascript/components/EmojiPicker/types';

type PreferenceValue = boolean | number | string | number[] | Tag[] | Emoji[];

type Preferences = {
  [key: string]: PreferenceValue;
};

const USER_KEY = `BugHerdUserSettings`;
const PREFERENCES_GET_ERROR_MESSAGE = `Couldn't get user preferences from local storage`;
const PREFERENCES_SET_ERROR_MESSAGE = `Couldn't set user preferences in local storage`;

const getSidebarPreference = ({
  projectId,
  preference,
  userSetting
}: {
  projectId?: number;
  preference?: string;
  userSetting?: boolean;
}) => {
  const settingsKey = userSetting ? USER_KEY : key(projectId || 0);
  return new Promise((resolve, reject) => {
    try {
      const token = uuid().toString();
      const cb = (event: MessageEvent) => {
        if (
          event.data.EVENT_NAME === 'GET_LOCAL_STORAGE_RESPONSE' &&
          token === event.data.payload.token
        ) {
          window.removeEventListener('message', cb);
          const settings = JSON.parse(event.data.payload.localData);

          if (!settings) {
            resolve(undefined);
            return;
          }

          resolve(preference ? settings[preference] : settings);
          return;
        }
      };

      window.addEventListener('message', cb);
      iframeOrExtPostMessage(
        {
          EVENT_NAME: 'GET_LOCAL_STORAGE',
          payload: { key: settingsKey, token }
        },
        '*'
      );
    } catch (err) {
      console.error(PREFERENCES_GET_ERROR_MESSAGE, err);
    }
  });
};

const setSidebarPreferences = ({
  projectId,
  preferences,
  userSetting
}: {
  projectId?: number;
  preferences: Preferences;
  userSetting?: boolean;
}) => {
  const settingsKey = userSetting ? USER_KEY : key(projectId || 0);
  try {
    getSidebarPreference({ projectId, userSetting }).then(
      (previousSettings: any) => {
        let newSettings: any = previousSettings || {};
        newSettings = { ...newSettings, ...preferences };
        iframeOrExtPostMessage(
          {
            EVENT_NAME: 'SET_LOCAL_STORAGE',
            payload: { key: settingsKey, value: JSON.stringify(newSettings) }
          },
          '*'
        );
      }
    );
  } catch (err) {
    console.error(PREFERENCES_SET_ERROR_MESSAGE, err);
  }
};

const localKey = (projectId: number) => `projectSettings_${projectId}`;

const getLocalPreference = ({
  projectId,
  preference,
  userSetting
}: {
  projectId?: number;
  preference?: string;
  userSetting?: boolean;
}) => {
  const settingsKey = userSetting ? USER_KEY : localKey(projectId || 0);
  return new Promise((resolve, reject) => {
    try {
      const preferences = JSON.parse(
        window.localStorage.getItem(settingsKey) || '{}'
      );
      resolve(preference ? preferences[preference] : preferences);
    } catch (err) {
      console.error(PREFERENCES_GET_ERROR_MESSAGE, err);
    }
  });
};

const setLocalPreferences = ({
  projectId,
  preferences,
  userSetting
}: {
  projectId?: number;
  preferences: Preferences;
  userSetting?: boolean;
}) => {
  const settingsKey = userSetting ? USER_KEY : localKey(projectId || 0);
  try {
    getLocalPreference({ projectId, userSetting }).then(previousSettings => {
      window.localStorage.setItem(
        settingsKey,
        JSON.stringify({ ...(previousSettings || {}), ...preferences })
      );
    });
  } catch (err) {
    console.error(PREFERENCES_SET_ERROR_MESSAGE, err);
  }
};

// function to set preferences without projectId
export const getGlobalPreferences = (
  preference?: string
): Promise<string | undefined> =>
  new Promise((resolve, reject) => {
    try {
      const preferences = JSON.parse(
        window.localStorage.getItem('globalPreferences') || '{}'
      );
      resolve(preference ? preferences[preference] : preferences);
    } catch (err) {
      console.error(PREFERENCES_GET_ERROR_MESSAGE, err);
    }
  });

// function to set preferences without projectId
export const setGlobalPreferences = (preferences: Preferences) => {
  try {
    const previousSettings = JSON.parse(
      window.localStorage.getItem('globalPreferences') || '{}'
    );
    const newSettings = { ...previousSettings, ...preferences };
    window.localStorage.setItem(
      'globalPreferences',
      JSON.stringify(newSettings)
    );
  } catch (err) {
    console.error(PREFERENCES_SET_ERROR_MESSAGE, err);
  }
};

export const getPreference = async ({
  projectId,
  preference,
  isAdminView,
  userSetting
}: {
  projectId?: number;
  preference?: string;
  isAdminView?: boolean;
  userSetting?: boolean;
}) =>
  isAdminView
    ? getLocalPreference({ projectId, preference, userSetting })
    : getSidebarPreference({ projectId, preference, userSetting });

export const setPreferences = ({
  projectId,
  preferences,
  isAdminView,
  userSetting
}: {
  projectId?: number;
  preferences: Preferences;
  isAdminView?: boolean;
  userSetting?: boolean;
}) =>
  isAdminView
    ? setLocalPreferences({ projectId, preferences, userSetting })
    : setSidebarPreferences({ projectId, preferences, userSetting });
